import { sortBy } from "lodash";
import API from "serviceshift-ui/api-client";
import { getProperty } from "@/lib/helpers.js";

import { EmailRecipient } from "@/EmailQuoteOrInvoice/types";
import { EmailTemplateCustomization } from "@/EmailTemplates/emailTemplate";
export default {
  approveInvoice({ dispatch }, params) {
    const { invoice, reason } = params;
    return API.makeRequest(invoice.links.invoice_approve, {
      method: "POST",
      data: {
        reason,
        invoice_format: invoice.format
      }
    }).then(() => {
      dispatch("refreshJobAndInvoice", null);
    });
  },
  createRefund({ dispatch }, { url, data }) {
    return API.makeRequest(url, {
      method: "POST",
      data
    }).then(() => {
      dispatch("refreshJobAndInvoice", null);
    });
  },
  createMultiInvoicePayment(_, { customerId, data }) {
    return API.makeRequest(`/v2/customers/${customerId}/invoice_payments`, {
      method: "POST",
      data,
      ignoreAppHandleAPIError: true
    });
  },
  createTransaction({ dispatch }, { invoice, data }) {
    return new Promise((resolve, reject) => {
      API.makeRequest(invoice.payment_create_url, {
        method: "POST",
        data
      })
        .then((res) => {
          dispatch("refreshJobAndInvoice", null);
          resolve(res);
        })
        .catch((err) => {
          // Refresh job and invoice on failed payments
          // to get a new create_payment link which has the
          // next_payment_key in the url
          dispatch("refreshJobAndInvoice", null);
          reject(err);
        });
    });
  },
  fetchInvoicePaymentSummary(_, { customerId, invoiceIds }) {
    return API.makeRequest(
      `/v2/customers/${customerId}/invoice_payments/summary`,
      {
        method: "POST",
        data: { invoice_ids: invoiceIds },
        ignoreAppHandleAPIError: true
      }
    ).then((res) => res.data);
  },
  fetchInvoice({ dispatch }, invoiceId) {
    return API.makeRequest(`/v2/invoices/${invoiceId}`, {
      method: "GET",
      params: {
        item_grouping: 1
      }
    }).then((res) => {
      dispatch("setCurrentInvoice", res.data);
      return res;
    });
  },
  async fetchInvoiceDateChangeSummary(_, { link, params }) {
    const res = await API.makeRequest(link, { method: "GET", params });
    return res.data;
  },
  fetchTransactions({ commit }, { payment_list_url }) {
    return API.makeRequest(payment_list_url, {
      method: "GET"
    }).then((res) => {
      commit("replace", {
        state: "transactions",
        data: res.data
      });
    });
  },
  setCurrentInvoice({ commit, state }, invoice) {
    if (
      invoice &&
      state.currentUser &&
      invoice.customer?.customer_id &&
      invoice.customer.customer_id !== state.currentUser.id
    ) {
      window.Sentry?.withScope((scope) => {
        scope.setLevel("warning");
        scope.setTag("ticket", "TOF-521");
        scope.setExtras({
          url: window.location.href,
          currentUserId: state.currentUser.id,
          currentUserName: state.currentUser.full_name,
          attemptedInvoiceId: invoice.invoice_id,
          attemptedInvoiceCustomerId: invoice.customer?.customer_id,
          attemptedInvoiceBillingCustomerId:
            invoice.billing_customer?.customer_id,
          previousInvoiceId: state.currentInvoice?.invoice_id ?? null
        });
        window.Sentry.captureMessage(
          "Invoice does not belong to current customer — clearing"
        );
      });
      commit("replace", { state: "currentInvoice", data: null });
      return;
    }

    commit("replace", {
      state: "currentInvoice",
      data: invoice
    });
    if (
      invoice &&
      Object.prototype.hasOwnProperty.call(invoice, "job_id") &&
      getProperty(state.job, "id") === getProperty(invoice, "job_id")
    ) {
      commit("update", {
        state: "job",
        data: { invoice }
      });
    }
  },
  setLineItemSoldTime({ dispatch }, { link, ...data }) {
    return API.makeRequest(link, {
      method: "PUT",
      data
    }).then((res) => {
      dispatch("refreshJobAndInvoice", null, res.data);
    });
  },
  async createEquipmentLink({ dispatch }, { link, data }) {
    const invoice = await API.makeRequest(link, { method: "POST", data });
    return dispatch("refreshInvoice", invoice.data);
  },
  async destroyEquipmentLink({ dispatch }, { link }) {
    const invoice = await API.makeRequest(link, { method: "DELETE" });
    return dispatch("refreshJobAndInvoice", null, invoice.data);
  },
  async updateEquipmentLink({ dispatch }, { link, data }) {
    const invoice = await API.makeRequest(link, { method: "PUT", data });
    return dispatch("refreshInvoice", invoice.data);
  },
  refreshJobAndInvoice({ dispatch, state, commit }, invoice, job) {
    // Do a full job replace
    if (job) {
      commit("replace", {
        state: "job",
        data: job
      });
      return;
    }
    const jobId = (invoice && invoice.job_id) || (state.job && state.job.id);
    if (jobId) {
      dispatch("fetchJob", {
        jobId: jobId
      });
    }
    return dispatch("refreshInvoice", invoice);
  },
  refreshInvoice({ state, dispatch }, invoice) {
    if (
      state.currentInvoice ||
      getProperty(state.job, "id") === getProperty(invoice, "job_id")
    ) {
      // Update currentInvoice with api response
      // or refetch invoice if no invoice was returned from api
      if (invoice) {
        dispatch("setCurrentInvoice", invoice);
      } else {
        dispatch("fetchInvoice", state.currentInvoice.invoice_id);
      }
    }
  },
  updateInvoiceNote({ dispatch }, transaction) {
    const { invoice_id, id, notes } = transaction;
    return API.makeRequest(`/v2/invoices/${invoice_id}/transactions/${id}`, {
      method: "PUT",
      data: {
        notes
      },
      params: { item_grouping: 1 }
    }).then(() => {
      dispatch("refreshJobAndInvoice", null);
    });
  },
  updateInvoiceDisplayMembershipSavings(
    { dispatch },
    { url, display_membership_savings }
  ) {
    return API.makeRequest(url, {
      method: "PUT",
      data: { display_membership_savings }
    }).then((res) => {
      dispatch("refreshJobAndInvoice", res.data);
    });
  },
  setReferenceId({ dispatch }, payload) {
    const { invoice_id, id, data } = payload;
    return API.makeRequest(
      `/v2/invoices/${invoice_id}/transactions/${id}/reference_id`,
      {
        method: "PUT",
        data,
        params: { item_grouping: 1 }
      }
    ).then(() => {
      dispatch("refreshJobAndInvoice", null);
    });
  },
  async updateInvoiceDetails({ dispatch }, { link, data }) {
    const res = await API.makeRequest(link, { method: "PUT", data });
    dispatch("setCurrentInvoice", res.data);
    return res.data;
  },
  updateInvoiceDate(_, { link, data }) {
    return API.makeRequest(link, { method: "PUT", data });
  },
  updateInvoiceSummary(_, { link, summary }) {
    return API.makeRequest(link, { method: "PUT", data: { summary } });
  },
  updateInvoiceBatching(_, { link, exclude_from_batching }) {
    return API.makeRequest(link, {
      method: "PUT",
      data: { exclude_from_batching }
    });
  },
  voidInvoice(_, { link, reason }) {
    return API.makeRequest(link, {
      method: "POST",
      data: { reason }
    });
  },
  unapproveInvoice(_, { link, reason }) {
    return API.makeRequest(link, {
      method: "PUT",
      data: { reason }
    });
  },
  async emailInvoice(
    _,
    params: {
      link: string;
      email_addresses?: string[];
      customizations?: EmailTemplateCustomization[];
    }
  ) {
    return API.makeRequest(params.link, {
      method: "POST",
      data: {
        additional_emails: params.email_addresses || [],
        customizations: (params.customizations || []).map((customization) => ({
          key: customization.key,
          value: customization.value
        }))
      }
    });
  },
  async getEmailRecipientsForInvoice(
    { dispatch },
    params: {
      id: number;
      customer_id: number;
    }
  ) {
    const invoiceResponse = await dispatch("fetchInvoice", params.id);
    const availableContactsResponse = await API.makeRequest<{
      emails: {
        required: {
          owner: string;
          email_address: string;
          label: string | null;
          checked: boolean;
        }[];
        optional: {
          owner: string;
          email_address: string;
          label: string | null;
          checked: boolean;
        }[];
      };
    }>(
      `/v2/customers/${params.customer_id}/addresses/${invoiceResponse.data.service_address.id}/contact`
    );
    return [
      ...sortBy(
        availableContactsResponse.data.emails.required.map((email) => {
          return new EmailRecipient({
            owner: email.owner as any,
            primary: true,
            name: email.label ? `${email.label}` : "",
            email_address: email.email_address,
            checked: email.checked
          });
        }),
        "email"
      ),
      ...sortBy(
        availableContactsResponse.data.emails.optional.map((email) => {
          return new EmailRecipient({
            owner: email.owner as any,
            primary: false,
            name: email.label ? `${email.label}` : "",
            email_address: email.email_address,
            checked: email.checked
          });
        }),
        "email"
      )
    ] as EmailRecipient[];
  }
};
